import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  name: string;
  background: string;
  reveal: string;
  coordinates: number[][];
  coordinates2?: number[];
}

interface LevelsFile {
  viewBox: string;
  levels: Level[];
}

const DATA = levelsData as LevelsFile;
const LEVELS = DATA.levels;
const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const ASSET_BASE = 'assets/gcompris-AGPL/drawnumbers';

export function mountDrawNumbers(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalPoints = level.coordinates.length;
  let nextPointIdx = 0;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Desenhe os números</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage dnum">
      <div class="feedback" aria-live="polite"></div>
      <p class="dnum__progress" aria-live="polite"></p>
      <p class="dnum__hint">Ligue os pontos em ordem pra desenhar: <strong>${level.name}</strong>.</p>
      <div class="dnum__stage-wrap"></div>
      <div class="dnum__actions">
        <button class="btn btn--secondary dnum__reset" type="button">↺ Reiniciar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.dnum__progress')!;
  const stageWrap = page.querySelector<HTMLElement>('.dnum__stage-wrap')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.dnum__reset')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    nextPointIdx = 0;
    render();
  });

  function renderProgress(): void {
    progressEl.textContent = `Próximo ponto: ${Math.min(nextPointIdx + 1, totalPoints)} de ${totalPoints}`;
  }

  function render(): void {
    renderProgress();
    stageWrap.innerHTML = '';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', DATA.viewBox);
    svg.setAttribute('class', 'dnum__svg');

    // Fundo sketch (paper)
    const bg = document.createElementNS(SVG_NS, 'image');
    const sketchUrl = `${ASSET_BASE}/${level.background}.svg`;
    bg.setAttributeNS(XLINK_NS, 'href', sketchUrl);
    bg.setAttribute('href', sketchUrl);
    bg.setAttribute('x', '0');
    bg.setAttribute('y', '0');
    bg.setAttribute('width', '540');
    bg.setAttribute('height', '480');
    bg.setAttribute('class', 'dnum__bg');
    bg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.appendChild(bg);

    // Fundo revelado (número grande/colorido)
    const bg2 = document.createElementNS(SVG_NS, 'image');
    const revealUrl = `${ASSET_BASE}/${level.reveal}.svg`;
    bg2.setAttributeNS(XLINK_NS, 'href', revealUrl);
    bg2.setAttribute('href', revealUrl);
    bg2.setAttribute('x', '0');
    bg2.setAttribute('y', '0');
    bg2.setAttribute('width', '540');
    bg2.setAttribute('height', '480');
    bg2.setAttribute('class', 'dnum__bg2');
    bg2.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    bg2.style.opacity = '0';
    svg.appendChild(bg2);

    // Linhas (só entre pontos do mesmo segmento)
    for (let i = 0; i < level.coordinates.length - 1; i++) {
      const segA = level.coordinates2?.[i] ?? 1;
      const segB = level.coordinates2?.[i + 1] ?? 1;
      if (segA !== segB) continue;
      const a = level.coordinates[i]!;
      const b = level.coordinates[i + 1]!;
      const drawn = i + 1 <= nextPointIdx;
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(a[0]));
      line.setAttribute('y1', String(a[1]));
      line.setAttribute('x2', String(b[0]));
      line.setAttribute('y2', String(b[1]));
      line.setAttribute('class', `dnum__line ${drawn ? 'dnum__line--drawn' : ''}`);
      svg.appendChild(line);
    }

    // Renderizar pontos em ordem: done primeiro, undone depois, NEXT por último (fica em cima)
    const order: number[] = [];
    for (let i = 0; i < level.coordinates.length; i++) if (i < nextPointIdx) order.push(i);
    for (let i = 0; i < level.coordinates.length; i++) if (i > nextPointIdx) order.push(i);
    if (nextPointIdx < level.coordinates.length) order.push(nextPointIdx);

    for (const idx of order) {
      const p = level.coordinates[idx]!;
      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', 'dnum__point');
      g.setAttribute('transform', `translate(${p[0]} ${p[1]})`);
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', '0');
      circle.setAttribute('cy', '0');
      circle.setAttribute('r', '11');
      let cls = 'dnum__point-circle';
      if (idx < nextPointIdx) cls += ' dnum__point-circle--done';
      else if (idx === nextPointIdx) cls += ' dnum__point-circle--next';
      circle.setAttribute('class', cls);
      g.appendChild(circle);
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('x', '0');
      text.setAttribute('y', '0');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('class', `dnum__point-text ${idx < nextPointIdx ? 'dnum__point-text--done' : ''}`);
      text.textContent = String(idx + 1);
      g.appendChild(text);
      g.addEventListener('click', () => onPointClick(idx));
      svg.appendChild(g);
    }

    stageWrap.appendChild(svg);
  }

  function onPointClick(idx: number): void {
    if (buttonsBlocked) return;
    if (idx !== nextPointIdx) {
      ctx.audio.play('wrong');
      flashWrong();
      return;
    }
    ctx.audio.play('click');
    nextPointIdx++;
    if (nextPointIdx >= totalPoints) {
      onComplete();
      return;
    }
    render();
  }

  function flashWrong(): void {
    feedback.textContent = '✗';
    feedback.className = 'feedback feedback--show feedback--err';
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 350);
  }

  function onComplete(): void {
    buttonsBlocked = true;
    render();
    const bg2 = stageWrap.querySelector<SVGElement>('.dnum__bg2');
    if (bg2) bg2.style.opacity = '1';
    ctx.audio.play('correct');
    feedback.textContent = '✓';
    feedback.className = 'feedback feedback--show feedback--ok';
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
    window.setTimeout(() => finishLevel(), 1500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, totalPoints);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você desenhou o número <strong>${level.name}</strong>.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo número</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/drawnumbers/${level.level + 1}`;
      });
    }
  }

  render();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}

